import React from "react"
import { Box, Stack, type BoxProps, type StackProps } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { colors, typeScale } from "../theme/tokens"
import {
  ViraCard,
  VIRA_CARD_PADDING,
  VIRA_CARD_RADIUS,
  type ViraCardProps,
} from "../atoms/ViraCard"
import { Heading } from "../atoms/ViraText"

const HEADER_BORDER = colors.vira.border

export type ContentCardHeaderProps = StackProps & {
  /** Card title — string uses Heading level="card" */
  title?: React.ReactNode
  /** Leading adornment (icon, help) before the title */
  titleStart?: React.ReactNode
  /** Trailing meta (date range, status) */
  meta?: React.ReactNode
  /** Trailing actions (buttons, chevron) */
  actions?: React.ReactNode
}

/**
 * Shared card header — same border, padding, and title size on every card.
 */
export function ContentCardHeader({
  title,
  titleStart,
  meta,
  actions,
  sx,
  children,
  ...rest
}: ContentCardHeaderProps) {
  const hasSlots = title != null || titleStart != null || meta != null || actions != null
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
      flexWrap="wrap"
      useFlexGap
      sx={composeSx(
        () => ({
          px: VIRA_CARD_PADDING,
          py: 2,
          borderBottom: `1px solid ${HEADER_BORDER}`,
          width: "100%",
          boxSizing: "border-box",
          minWidth: 0,
        }),
        sx,
      )}
      {...rest}
    >
      {hasSlots ? (
        <>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0, flex: "1 1 auto" }}
          >
            {titleStart}
            {typeof title === "string" ? (
              <Heading level="card">{title}</Heading>
            ) : (
              title
            )}
          </Stack>
          {meta != null || actions != null ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ flexShrink: 0, maxWidth: "100%" }}
            >
              {meta}
              {actions}
            </Stack>
          ) : null}
        </>
      ) : (
        children
      )}
    </Stack>
  )
}

export type ContentCardBodyProps = BoxProps

export function ContentCardBody({ sx, children, ...rest }: ContentCardBodyProps) {
  return (
    <Box
      sx={composeSx(
        () => ({
          px: VIRA_CARD_PADDING,
          py: 2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }),
        sx,
      )}
      {...rest}
    >
      {children}
    </Box>
  )
}

export type ContentCardFooterProps = BoxProps

export function ContentCardFooter({
  sx,
  children,
  ...rest
}: ContentCardFooterProps) {
  return (
    <Box
      sx={composeSx(
        () => ({
          px: VIRA_CARD_PADDING,
          py: 2,
          borderTop: `1px solid ${HEADER_BORDER}`,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          fontSize: typeScale.meta,
        }),
        sx,
      )}
      {...rest}
    >
      {children}
    </Box>
  )
}

export type ContentCardProps = Omit<ViraCardProps, "padded" | "children"> & {
  title?: React.ReactNode
  titleStart?: React.ReactNode
  meta?: React.ReactNode
  actions?: React.ReactNode
  footer?: React.ReactNode
  /** Optional custom header (overrides title/meta/actions slots) */
  header?: React.ReactNode
  children?: React.ReactNode
  bodySx?: BoxProps["sx"]
  headerSx?: StackProps["sx"]
  footerSx?: BoxProps["sx"]
}

/**
 * Canonical Client Detail content card: Header / Body / Footer.
 * Use this for charts, snapshots, plans, notes — same chrome everywhere.
 */
function ContentCardRoot({
  title,
  titleStart,
  meta,
  actions,
  footer,
  header,
  children,
  bodySx,
  headerSx,
  footerSx,
  sx,
  ...rest
}: ContentCardProps) {
  const showHeader =
    header != null ||
    title != null ||
    titleStart != null ||
    meta != null ||
    actions != null

  return (
    <ViraCard
      sx={composeSx(
        () => ({
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          borderRadius: VIRA_CARD_RADIUS,
        }),
        sx,
      )}
      {...rest}
    >
      {showHeader ? (
        header != null ? (
          header
        ) : (
          <ContentCardHeader
            title={title}
            titleStart={titleStart}
            meta={meta}
            actions={actions}
            sx={headerSx}
          />
        )
      ) : null}
      {children != null ? (
        <ContentCardBody sx={bodySx}>{children}</ContentCardBody>
      ) : null}
      {footer != null ? (
        <ContentCardFooter sx={footerSx}>{footer}</ContentCardFooter>
      ) : null}
    </ViraCard>
  )
}

export const ContentCard = Object.assign(ContentCardRoot, {
  Header: ContentCardHeader,
  Body: ContentCardBody,
  Footer: ContentCardFooter,
})